import { Col, Container, Row } from "react-grid-system";
import {
  centsToDollarString,
  convertDateStringToMonthDayYearFormat,
  convertYearMonthDateStringToMonthYearFormat,
  formatInCentralTime,
  validateBeforeCentsToDollarString,
} from "../../lib/formatting.ts";
import { useUpdateAccountLockStatus } from "../../lib/api/queries.ts";
import {
  useIsAgentRoleUnlock,
  useIsAgentRoleViewAccountNumber,
} from "../../lib/roleContext.ts";

import AccountDetailsHeaderRow from "../DetailsRow/index.ts";
import {
  ForecastMPDHoverData,
  GetAccountDetailsResponse,
  GetAccountResponse,
} from "../../lib/api/models.ts";
import Spinner from "../common/Spinner/index.ts";
import { StyledButton } from "../common/buttons/StyledButton/StyledButton.tsx";
import ToolLoadError from "../common/ToolLoadError/index.ts";
import autopayEnrollmentIcon from "../../assets/autopay-enrollment.svg";
import classNames from "classnames";
import { getDisplayError } from "../../lib/api/endpoints.ts";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./AccountDetailsHeader.module.css";
import { useState } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import ActionEntryPanel from "../ActionEntryPanel/ActionEntryPanel.tsx";
import Tooltip from "../common/Tooltip/Tooltip.tsx";

const strings = {
  accountDetailsHeader: "Account Details Header",
  accountIdAndCardNumber: "Account ID / Card #",
  activate: "Activate",
  activated: "Activated",
  amountDelq: "Amt Delq",
  amountDue: "Amt Due",
  authFlag: "Auth Flag",
  authorizedUser: "Authorized User",
  cmInfo: "CM Info",
  credit: "Credit",
  creditAvail: "Credit Avail",
  creditLimit: "Credit Limit",
  currentBalance: "Current Balance",
  currentMonthPayment: "Current Mo Payment",
  delinquent: "Delinquent #Days/Bucket",
  employee: "Employee/VIP Ind",
  enrolledAutopay: "Autopay Enrolled",
  fixPaymentAmount: "Fix Pmt Amount",
  intExtStatus: "Int/Ext Status",
  lastContactDateCode: "Last Contact Date / Code",
  locked: "Locked",
  manage: "Manage",
  minPayDue: "Min Pay Due",
  nextCycle: "Next Cycle / Stmt Dt",
  no: "No",
  notEnrolledAutopay: "Not Autopay Enrolled",
  oclAmount: "OCL Amount",
  openDate: "Open Date",
  partyId: "Party ID",
  paymentDueDate: "Pmt Due Date",
  permanentQueue: "Permanent Queue",
  preselected: "Preselected for C2C on Web",
  prev1: "Prev 1",
  prev2: "Prev 2",
  prev3: "Prev 3",
  p2d: "P2D",
  registrationDateTime: "Registered On",
  securityHintWord: "Security Hint/Word",
  special: "Special Hdlg Ind",
  state: "State",
  statusReason: "Status Reason Code",
  temporaryQueue: "Temporary Queue",
  totalAmountDue: "Total Amount Due",
  transferReason: "Transfer Reason",
  unlock: "Unlock",
  unlocked: "Unlocked",
  webStatus: "Web Status",
  yes: "Yes",
  mastercardAbbreviated: "M",
  visaAbbreviated: "V",
  username: "Username",
  close: "Close",

  lastStatementMPDDescription: "Last Stmt MPD Desc",

  minPayDueHoverTitle: "MPD Calc Mthd",

  negAmHeader:
    "To Prevent NegAm Payment Amounts MUST be Greater than or Equal to:",
  negAmFooter:
    "*If account is OCL ensure your Cure Program Payments are Greater than or Equal to the 2nd and 3rd payments",
  negAm1st: "1st Pmt = ",
  negAm2nd: "*2nd Pmt = ",
  negAm3rd: "*3rd+ Pmt = ",
};

function getCardTypeAbbreviated(cardNumber?: string | null): string {
  if (!cardNumber) {
    return "";
  }
  if (cardNumber.startsWith("2") || cardNumber.startsWith("5")) {
    return strings.mastercardAbbreviated;
  } else if (cardNumber.startsWith("4")) {
    return strings.visaAbbreviated;
  }
  return "";
}

function isSpecialState(state: string): boolean {
  switch (state) {
    case "CA":
    case "DC":
    case "FL":
    case "HI":
    case "MA":
    case "MD":
    case "NC":
    case "NY":
    case "PA":
    case "SC":
    case "TX":
    case "WV":
      return true;
  }
  return false;
}

function NegAmHover(data: ForecastMPDHoverData) {
  return (
    <div>
      <p>
        {strings.negAm1st}
        {centsToDollarString(data.firstPaymentAmountCents, false, true)}
      </p>
      <p>
        {strings.negAm2nd}
        {centsToDollarString(data.secondPaymentAmountCents, false, true)}
      </p>
      <p>
        {strings.negAm3rd}
        {centsToDollarString(data.thirdPaymentAmountCents, false, true)}
      </p>
    </div>
  );
}

type Props = {
  account: GetAccountResponse;
  accountDetailsQueryResult: UseQueryResult<GetAccountDetailsResponse>;
  className?: string;
  onDisplayGeneralErrors: (error: unknown) => void;
};

export default function AccountDetailsHeader({
  account,
  accountDetailsQueryResult: {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  },
  className,
  onDisplayGeneralErrors,
}: Props) {
  const canViewAccountNumber = useIsAgentRoleViewAccountNumber();

  const [showCardNumber, setShowCardNumber] = useState(false);

  const {
    mutateAsync: updateAccountLockStatus,
    isLoading: isUpdateAccountLockStatusLoading,
  } = useUpdateAccountLockStatus();

  const renderActivationStatus = (activated: boolean) => {
    if (activated) {
      return <span>{strings.yes}</span>;
    } else {
      return (
        <div className={styles.activationStatusContainer}>
          <div className={styles.noActivation}>{strings.no}</div>
        </div>
      );
    }
  };

  const canUnlockCardmember = useIsAgentRoleUnlock();

  const renderAbbreviatedCardNumber = (cardNumber?: string | null) => {
    if (!cardNumber) {
      return <span className={styles.cardNumberButton}>-</span>;
    }

    if (!canViewAccountNumber || showCardNumber) {
      return (
        <span className={styles.cardNumberButton}>
          {`${getCardTypeAbbreviated(cardNumber)}${cardNumber?.slice(12)}`}
        </span>
      );
    }

    return (
      <StyledButton
        className={styles.cardNumberButton}
        onClick={() => setShowCardNumber(true)}
        variant="transparent"
      >
        {getCardTypeAbbreviated(cardNumber)}
        {cardNumber?.slice(12)}
      </StyledButton>
    );
  };

  return (
    <div
      className={classNames(toolStyles.container, styles.container, className)}
    >
      <Container>
        {isLoading ? (
          <Row>
            <Col>
              <Spinner className={styles.spinner} />
            </Col>
          </Row>
        ) : isError ? (
          <Row>
            <Col>
              <ToolLoadError
                toolName={strings.accountDetailsHeader}
                errorDescription={getDisplayError(error)?.message}
                onRetry={refetch}
              />
            </Col>
          </Row>
        ) : (
          <Row>
            <Col xs={3}>
              <div className={styles.headerRow}>
                <span className={styles.headerRowBoldValue}>{data.name}</span>
                <span className={styles.headerRowBoldValue}>
                  {strings.credit}
                </span>
              </div>
              <div className={styles.accountIdAndCardNumberContainer}>
                <div className={styles.label}>
                  {strings.accountIdAndCardNumber}
                </div>
                <div className={styles.accountIdAndCardNumber}>
                  <div className={classNames(styles.value, styles.flexRow)}>
                    <span>{`${data.currentDwbuid} /`}</span>
                    {renderAbbreviatedCardNumber(data.cardNumber)}
                  </div>
                  {showCardNumber ? (
                    <div className={classNames(styles.value, styles.flexRow)}>
                      <div className={styles.cardNumber}>{data.cardNumber}</div>
                      <StyledButton
                        className={styles.cardNumberButton}
                        onClick={() => setShowCardNumber(false)}
                        variant="transparent"
                      >
                        {strings.close}
                      </StyledButton>
                    </div>
                  ) : null}
                </div>
              </div>

              <AccountDetailsHeaderRow
                label={strings.authorizedUser}
                value={data.authorizedUser}
              />
              <AccountDetailsHeaderRow label={strings.p2d} value={data.p2d} />
              <AccountDetailsHeaderRow
                label={strings.securityHintWord}
                value={`${data.securityHint}/${data.securityWord}`}
              />

              <AccountDetailsHeaderRow
                label={strings.lastContactDateCode}
                value={`${convertDateStringToMonthDayYearFormat(data.lastContactDate)} / ${data.lastContactCode}`}
              />
              <AccountDetailsHeaderRow
                label={strings.state}
                value={data.stateCode}
                valueClassName={
                  isSpecialState(data.stateCode ?? "")
                    ? styles.headerRowValueRed
                    : undefined
                }
              />
              <AccountDetailsHeaderRow
                label={strings.delinquent}
                value={`${data.daysDelinquent}/${data.delinquentBucket}`}
              />
              <AccountDetailsHeaderRow
                label={strings.temporaryQueue}
                value={data.temporaryQueue}
              />
              <AccountDetailsHeaderRow
                label={strings.permanentQueue}
                value={data.permanentQueue}
              />
            </Col>
            <Col xs={3}>
              <div className={styles.headerRow}>
                <span className={styles.headerRowValue}>
                  {strings.currentBalance}
                </span>
                <span className={styles.headerRowBoldValue}>
                  {validateBeforeCentsToDollarString(data.currentBalanceCents)}
                </span>
              </div>
              <AccountDetailsHeaderRow
                label={strings.creditLimit}
                value={validateBeforeCentsToDollarString(data.creditLimitCents)}
              />
              <AccountDetailsHeaderRow
                label={strings.creditAvail}
                value={validateBeforeCentsToDollarString(
                  data.creditAvailableCents,
                )}
              />
              <AccountDetailsHeaderRow
                label={strings.totalAmountDue}
                value={validateBeforeCentsToDollarString(
                  data.totalAmountDueCents,
                )}
              />
              <AccountDetailsHeaderRow
                label={strings.amountDue}
                value={validateBeforeCentsToDollarString(data.amountDueCents)}
              />
              <AccountDetailsHeaderRow
                label={strings.amountDelq}
                value={validateBeforeCentsToDollarString(
                  data.delinquentAmountCents,
                )}
              />
              <AccountDetailsHeaderRow
                label={strings.oclAmount}
                value={validateBeforeCentsToDollarString(
                  data.oclAmountCents,
                  false,
                  true,
                )}
              />
              <AccountDetailsHeaderRow
                label={strings.minPayDue}
                value={data.minPayDueAmount}
                tooltip={
                  data.forecastMPDHoverData ? (
                    <Tooltip
                      header={strings.negAmHeader}
                      footer={strings.negAmFooter}
                    >
                      <NegAmHover {...data.forecastMPDHoverData} />
                    </Tooltip>
                  ) : (
                    <Tooltip header={strings.minPayDueHoverTitle}>
                      {data.minPayDueAmountHoverDescription}
                    </Tooltip>
                  )
                }
              />
              <AccountDetailsHeaderRow
                label={strings.currentMonthPayment}
                value={validateBeforeCentsToDollarString(
                  data.currentMonthPaymentCents,
                )}
                tooltip={
                  <Tooltip header={strings.lastStatementMPDDescription}>
                    {data.bothDataPanel.lastStatementMPDDescription}
                  </Tooltip>
                }
              />
              <AccountDetailsHeaderRow
                label={strings.fixPaymentAmount}
                value={validateBeforeCentsToDollarString(data.fixPaymentCents)}
              />
            </Col>
            <Col xs={3}>
              <AccountDetailsHeaderRow
                label={strings.paymentDueDate}
                value={convertDateStringToMonthDayYearFormat(
                  data.paymentDueDate,
                )}
              />
              <AccountDetailsHeaderRow
                label={strings.nextCycle}
                value={convertDateStringToMonthDayYearFormat(
                  data.nextCycleDate,
                )}
              />
              <AccountDetailsHeaderRow label={strings.transferReason} />
              <AccountDetailsHeaderRow
                label={strings.employee}
                value={data.employeeVipStatus}
                valueClassName={
                  data.employeeVipStatus === "Yes"
                    ? styles.headerRowValueRed
                    : undefined
                }
              />
              <AccountDetailsHeaderRow
                label={strings.special}
                value={data.specialHandlingIndicator}
              />
              <AccountDetailsHeaderRow
                label={strings.prev1}
                value={data.prevDwbuid}
              />
              <AccountDetailsHeaderRow
                label={strings.prev2}
                value={data.prevDwbuid2}
              />
              <AccountDetailsHeaderRow
                label={strings.prev3}
                value={data.prevDwbuid3}
              />
              <AccountDetailsHeaderRow
                label={strings.partyId}
                value={data.partyId}
              />
              <AccountDetailsHeaderRow
                label={strings.preselected}
                value={data.isC2CPreselected ? strings.yes : strings.no}
              />
            </Col>
            <Col xs={3}>
              <AccountDetailsHeaderRow
                label={strings.intExtStatus}
                value={data.internalExternalStatus}
              />
              <AccountDetailsHeaderRow
                label={strings.statusReason}
                value={data.reasonCodeDescription}
              />
              <AccountDetailsHeaderRow
                label={strings.openDate}
                value={convertYearMonthDateStringToMonthYearFormat(
                  data.openDate,
                )}
              />
              <AccountDetailsHeaderRow
                label={strings.username}
                value={data.username}
              />
              <AccountDetailsHeaderRow
                label={strings.registrationDateTime}
                value={
                  data.registrationDateTime
                    ? formatInCentralTime(
                        new Date(data.registrationDateTime),
                        "MM/dd/yyyy hh:mmaa",
                      )
                    : null
                }
              />
              <div className={styles.accountDetailsHeaderRow}>
                <span className={styles.label}>{strings.activated}</span>
                <div className={styles.value}>
                  {renderActivationStatus(data.activated)}
                </div>
              </div>
              <div
                className={classNames({
                  [styles.infoContainer]: true,
                  [styles.greenBackground]: data.isEnrolledAutopay,
                })}
              >
                <div className={styles.infoStatusWithImage}>
                  <img
                    className={styles.autopayEnrollmentIcon}
                    src={autopayEnrollmentIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <span>
                    {data.isEnrolledAutopay
                      ? strings.enrolledAutopay
                      : strings.notEnrolledAutopay}
                  </span>
                </div>
              </div>
              <div className={styles.infoContainer}>
                <div className={styles.infoStatus}>
                  <span>{`${strings.webStatus} - ${
                    data.isLocked ? strings.locked : strings.unlocked
                  }`}</span>
                </div>
                {data.isLocked && canUnlockCardmember ? (
                  <StyledButton
                    className={styles.unlockButton}
                    variant="transparent"
                    onClick={() =>
                      updateAccountLockStatus({
                        dwbuid: account.currentDwbuid,
                      }).catch(onDisplayGeneralErrors)
                    }
                    isLoading={isUpdateAccountLockStatusLoading || isRefetching}
                  >
                    {strings.unlock}
                  </StyledButton>
                ) : null}
              </div>
              <div className={styles.infoContainer}>
                <div className={styles.infoStatus}>
                  <span>{`${strings.authFlag}${data.authFlag ? " - " + data.authFlag : ""}`}</span>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
      {!isLoading && !isError && (
        <ActionEntryPanel data={data.actionEntryPanel} />
      )}
    </div>
  );
}
